/**
 * Smooth-scrolls to the element matching a section id.
 * @param sectionId - Element id, optionally with a leading `#`
 */
export function scrollToSectionId(sectionId: string | null | undefined): void {
  const id = sectionId?.replace(/^#/, '');
  if (!id) {
    return;
  }
  setTimeout(() => {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }
    const container = element.closest('.view-content');
    if (container instanceof HTMLElement) {
      const offset =
        element.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop -
        16;
      container.scrollTo({ top: offset, behavior: 'smooth' });
      return;
    }
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, 0);
}

/**
 * Reads a hash fragment from a router URL.
 * @param url - Router URL
 * @returns Fragment without `#`, or null
 */
export function fragmentFromUrl(url: string): string | null {
  return url.split('#')[1] ?? null;
}

/**
 * Smooth-scrolls to the fragment of a router URL.
 * @param url - Router URL or location href
 */
export function scrollToUrlFragment(url: string = globalThis.location.href): void {
  scrollToSectionId(fragmentFromUrl(url) ?? globalThis.location.hash);
}

/**
 * Observes which section sits closest to the top of a scroll container.
 * @param container - Scroll container
 * @param sectionIds - Section element ids in document order
 * @param onChange - Called when the active section changes
 * @param options - Observation options
 * @param options.emitInitial - When true, emits the current section immediately
 * @returns Teardown function
 */
export function observeScrollSections(
  container: HTMLElement,
  sectionIds: readonly string[],
  onChange: (sectionId: string) => void,
  options: { emitInitial?: boolean } = {}
): () => void {
  const emitInitial = options.emitInitial !== false;
  let frame = 0;
  let lastEmitted: string | null = null;
  const read = (): void => {
    frame = 0;
    const marker = container.getBoundingClientRect().top + 48;
    let active = sectionIds[0];
    if (!active) {
      return;
    }
    for (const id of sectionIds) {
      const element = document.getElementById(id);
      if (!element) {
        continue;
      }
      if (element.getBoundingClientRect().top <= marker) {
        active = id;
      }
    }
    if (active === lastEmitted) {
      return;
    }
    lastEmitted = active;
    onChange(active);
  };
  const onScroll = (): void => {
    if (frame) {
      return;
    }
    frame = requestAnimationFrame(read);
  };
  container.addEventListener('scroll', onScroll, { passive: true });
  if (emitInitial) {
    read();
  }
  return () => {
    container.removeEventListener('scroll', onScroll);
    if (frame) {
      cancelAnimationFrame(frame);
    }
  };
}
